import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  DiscountConfig,
  DiscountCode,
  getDiscountConfig,
  updateDiscountConfig,
  subscribeToDiscountConfig
} from '../services/discountService';

// Discount Hook Result Interface
interface UseDiscountsResult {
  discountCodes: DiscountCode[];
  discountConfig: DiscountConfig;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  updateDiscountCode: (discountId: string, discountData: Partial<DiscountCode>) => Promise<void>;
  addDiscountCode: (discountData: Omit<DiscountCode, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  deleteDiscountCode: (discountId: string) => Promise<void>;
}

export const useDiscounts = (): UseDiscountsResult => {
  const [discountConfig, setDiscountConfig] = useState<DiscountConfig>({ discountCodes: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Convert discount config to array format for easier rendering
  const discountCodes = discountConfig.discountCodes || [];

  // Initial load and real-time subscription
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const initializeDiscounts = async () => {
      try {
        setLoading(true);
        setError(null);

        // Subscribe to real-time updates
        unsubscribe = subscribeToDiscountConfig((config) => {
          setDiscountConfig(config);
          setLoading(false);
        });

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load discount codes';
        setError(errorMessage);
        setLoading(false);
        console.error('Error initializing discount codes:', err);
      }
    };

    initializeDiscounts();

    // Cleanup subscription on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Refresh function to manually reload discount data
  const refresh = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const config = await getDiscountConfig();
      setDiscountConfig(config);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh discount codes';
      setError(errorMessage);
      console.error('Error refreshing discount codes:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update discount code function
  const updateDiscountCode = async (discountId: string, discountData: Partial<DiscountCode>): Promise<void> => {
    try {
      const updatedDiscountCodes = discountCodes.map(discount => 
        discount.id === discountId 
          ? { ...discount, ...discountData, updatedAt: new Date() }
          : discount
      );

      const updatedConfig = { ...discountConfig, discountCodes: updatedDiscountCodes };
      await updateDiscountConfig(updatedConfig);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update discount code';
      setError(errorMessage);
      console.error('Error updating discount code:', err);
      throw err;
    }
  };

  // Add discount code function
  const addDiscountCode = async (discountData: Omit<DiscountCode, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> => {
    try {
      const newDiscountCode: DiscountCode = {
        ...discountData,
        id: `discount-${Date.now()}-${Math.random().toString(36).substring(2)}`,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const updatedDiscountCodes = [...discountCodes, newDiscountCode];
      const updatedConfig = { ...discountConfig, discountCodes: updatedDiscountCodes };
      await updateDiscountConfig(updatedConfig);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add discount code';
      setError(errorMessage);
      console.error('Error adding discount code:', err);
      throw err;
    }
  };

  // Delete discount code function
  const deleteDiscountCode = async (discountId: string): Promise<void> => {
    try {
      const updatedDiscountCodes = discountCodes.filter(discount => discount.id !== discountId);
      const updatedConfig = { ...discountConfig, discountCodes: updatedDiscountCodes };
      await updateDiscountConfig(updatedConfig);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete discount code';
      setError(errorMessage);
      console.error('Error deleting discount code:', err);
      throw err;
    }
  };

  return {
    discountCodes,
    discountConfig,
    loading,
    error,
    refresh,
    updateDiscountCode,
    addDiscountCode,
    deleteDiscountCode
  };
}; 